import { useSyncExternalStore } from "react"

export interface PuzzleLevelStore {
  resyncFromStorage: ()=> void
  setLevel: (id: number)=> void
  useLevel: ()=> number
}

/**
 * Tiny store sharing one game's current puzzle id between the game page and
 * the sidebar panel. Persists to localStorage so both surfaces (and a later
 * reload) agree on the level, and notifies live subscribers on change.
 */
export function createPuzzleLevelStore(input: {
  fallbackId: number
  isValid: (id: number)=> boolean
  storageKey: string
}): PuzzleLevelStore {
  const listeners = new Set<()=> void>()
  const readStoredId = (): number=> {
    const stored = localStorage.getItem(input.storageKey)
    if (stored) {
      const id = parseInt(stored, 10)
      if (input.isValid(id)) return id
    }
    return input.fallbackId
  }
  const normalizeId = (id: number): number=> {
    if (input.isValid(id)) return id
    return input.fallbackId
  }
  let level = readStoredId()

  return {
    // Re-reads the persisted level now that `isValid` can resolve it. Used by
    // lazily-loading puzzle sets (10-colors): the stored id may have been
    // seeded before the puzzle data arrived, so the initial read fell back to
    // fallbackId — resync corrects it once the set is ready. No-op for
    // statically-loaded sets (the initial read already resolved the id).
    resyncFromStorage(): void {
      const stored = readStoredId()
      if (stored === level) return
      level = stored
      for (const listener of listeners) listener()
    },
    setLevel(id: number): void {
      const next = normalizeId(id)
      if (next === level) return
      level = next
      localStorage.setItem(input.storageKey, String(next))
      for (const listener of listeners) listener()
    },
    useLevel(): number {
      return useSyncExternalStore(subscribe, getSnapshot, getSnapshot)
    },
  }

  function getSnapshot() {
    return level
  }

  function subscribe(listener: ()=> void) {
    listeners.add(listener)
    return ()=> {
      listeners.delete(listener)
    }
  }
}